import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Layout, PageHeader } from "@/components/Layout";
import { fmtRM, formulaLabel } from "@/lib/format";
import { StatusBadge, RiskBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { ArrowLeft, PlusCircle, Trash2, Loader2, MessageCircle, Printer, Pencil, Receipt, Ban } from "lucide-react";
import { printReceipt, printStatement } from "@/lib/print";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { BAD_DEBT_REASONS, ADMIN_ROLES } from "@/lib/badDebtReasons";

const RISK_TAGS = ["None", "Bankrupt", "Lost Contact"];

function Stat({ label, value, accent }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">{label}</div>
      <div className={`font-mono-fig text-xl font-bold mt-1 ${accent || "text-slate-900"}`}>{value}</div>
    </div>
  );
}

export default function LoanDetail() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [showPay, setShowPay] = useState(false);
  const [pay, setPay] = useState({ date: new Date().toISOString().slice(0, 10), incomeAmount: "", principalAmount: "" });
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(false);
  const [sending, setSending] = useState(false);
  const [editInfo, setEditInfo] = useState(false);
  const [info, setInfo] = useState({ phone: "", address: "", icPassport: "" });
  const [editType, setEditType] = useState(false);
  const [typeForm, setTypeForm] = useState({ loanType: "personal", cycleDays: "", dueDay: "", nextDue: "" });
  const [editDetails, setEditDetails] = useState(false);
  const [detailsForm, setDetailsForm] = useState({ stamping: "", commission: "", riskTag: "None" });
  const [showBadDebt, setShowBadDebt] = useState(false);
  const [bdForm, setBdForm] = useState({ reason: BAD_DEBT_REASONS[0], note: "" });
  const { user } = useAuth();
  const { t: tr, tv } = useLang();
  const isAdmin = ADMIN_ROLES.includes(user?.role);
  const canEdit = user?.canEdit;
  const [loadError, setLoadError] = useState(null);

  const load = useCallback(
    () =>
      api
        .get(`/loans/${code}`)
        .then((res) => {
          setData(res.data);
          setLoadError(null);
        })
        .catch((err) => setLoadError(err.response?.status === 404 ? "notfound" : "error")),
    [code]
  );

  useEffect(() => { setData(null); setLoadError(null); load(); }, [load]);

  const addPayment = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/payments", {
        loanCode: code,
        date: pay.date,
        incomeAmount: parseFloat(pay.incomeAmount) || 0,
        principalAmount: parseFloat(pay.principalAmount) || 0,
      });
      toast.success("Payment recorded.");
      setShowPay(false);
      setPay({ date: new Date().toISOString().slice(0, 10), incomeAmount: "", principalAmount: "" });
      await load();
      setFlash(true);
      setTimeout(() => setFlash(false), 600);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const submitBadDebt = async () => {
    if (!bdForm.reason) return toast.error("Select a reason.");
    try {
      const { data: res } = await api.post("/bad-debt/request", {
        loanCode: code, reason: bdForm.reason, note: bdForm.note,
      });
      if (res.applied) toast.success("Loan marked as Bad Debt.");
      else toast.success("Bad-debt request sent to the director for approval.");
      setShowBadDebt(false);
      setBdForm({ reason: BAD_DEBT_REASONS[0], note: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to submit");
    }
  };
  const reactivate = async () => {
    try {
      await api.put(`/loans/${code}`, { status: "Active" });
      toast.success("Loan re-activated");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };
  const removePayment = async (pid) => {
    await api.delete(`/payments/${pid}`);
    toast.success("Payment removed");
    load();
  };
  const deleteLoan = async () => {
    if (!window.confirm(tr("ld.delete_confirm"))) return;
    await api.delete(`/loans/${code}`);
    toast.success("Loan deleted and ledger reversed");
    navigate("/loans");
  };

  const sendReminder = async () => {
    // Open WhatsApp (app/web) with the customer's number + pre-filled reminder.
    // Open the tab synchronously to avoid popup blockers, then set its URL.
    const win = window.open("", "_blank");
    setSending(true);
    try {
      const { data } = await api.get(`/reminders/whatsapp-link/${code}`);
      if (win) win.location.href = data.url;
      else window.location.href = data.url;
    } catch (err) {
      if (win) win.close();
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to open WhatsApp");
    } finally {
      setSending(false);
    }
  };

  const startEditInfo = () => {
    setInfo({ phone: data.loan.phone || "", address: data.loan.address || "", icPassport: data.loan.icPassport || "" });
    setEditInfo(true);
  };
  const saveInfo = async () => {
    try {
      await api.put(`/loans/${code}`, info);
      toast.success("Customer details updated.");
      setEditInfo(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to update");
    }
  };

  const startEditType = () => {
    setTypeForm({
      loanType: data.loan.loanType || "personal",
      cycleDays: data.loan.cycleDays != null ? String(data.loan.cycleDays) : "",
      dueDay: data.loan.dueDay != null ? String(data.loan.dueDay) : "",
      nextDue: data.loan.nextDueOverride || "",
    });
    setEditType(true);
  };
  const saveType = async () => {
    const payload = { loanType: typeForm.loanType, nextDueOverride: typeForm.nextDue || "" };
    if (typeForm.loanType === "business") {
      const cd = parseInt(typeForm.cycleDays, 10);
      if (!cd || cd <= 0) { toast.error("Enter a valid cycle (days) for a business loan."); return; }
      payload.cycleDays = cd;
    } else {
      const dd = parseInt(typeForm.dueDay, 10);
      if (!dd || dd < 1 || dd > 31) { toast.error("Enter a valid salary day (1–31)."); return; }
      payload.dueDay = dd;
    }
    try {
      await api.put(`/loans/${code}`, payload);
      toast.success("Payment schedule updated.");
      setEditType(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to update");
    }
  };

  const startEditDetails = () => {
    setDetailsForm({
      stamping: data.loan.stamping != null ? String(data.loan.stamping) : "",
      commission: data.loan.commission != null ? String(data.loan.commission) : "",
      riskTag: data.loan.riskTag || "None",
    });
    setEditDetails(true);
  };
  const saveDetails = async () => {
    const stamping = parseFloat(detailsForm.stamping) || 0;
    const commission = parseFloat(detailsForm.commission) || 0;
    if (stamping < 0 || commission < 0) { toast.error("Stamping and Commission cannot be negative."); return; }
    try {
      await api.put(`/loans/${code}`, { stamping, commission, riskTag: detailsForm.riskTag });
      toast.success("Loan details updated.");
      setEditDetails(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to update");
    }
  };

  if (loadError)
    return (
      <Layout>
        <button
          type="button"
          data-testid="loan-notfound-back"
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/loans"))}
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-6"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <div
          data-testid="loan-not-found"
          className="max-w-md mx-auto mt-16 bg-white border border-slate-200 rounded-xl p-8 text-center"
        >
          <div className="h-12 w-12 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center mx-auto mb-4">
            <Ban className="h-6 w-6" />
          </div>
          <h2 className="font-heading font-semibold text-slate-900 text-lg">
            {loadError === "notfound" ? "Loan not found" : "Unable to load loan"}
          </h2>
          <p className="text-sm text-slate-500 mt-1.5">
            {loadError === "notfound" ? (
              <>No loan matches the code <span className="font-mono-fig text-slate-700">{code}</span>. It may have been deleted, or it is outside the branches you can access.</>
            ) : (
              <>Something went wrong while loading this loan. Please try again.</>
            )}
          </p>
          <button
            data-testid="loan-notfound-goto-loans"
            onClick={() => navigate("/loans")}
            className="mt-5 bg-slate-900 text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-slate-800 transition-colors"
          >
            Back to loans
          </button>
        </div>
      </Layout>
    );
  if (!data) return <Layout><div className="text-slate-400 animate-pulse">Loading…</div></Layout>;
  const l = data.loan;
  const total = (parseFloat(pay.incomeAmount) || 0) + (parseFloat(pay.principalAmount) || 0);
  const parsed = l.parsedFormula || {};
  const inputCls = "w-full bg-white border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-slate-900 outline-none";
  const nextDue = computeNextDue(l, data.payments);

  return (
    <Layout>
      <button
        type="button"
        data-testid="loan-detail-back"
        onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/loans"))}
        className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <PageHeader
        title={
          <span className="flex items-center gap-3">
            <span className="font-mono-fig">{l.customerCode || l.code}</span>
            <StatusBadge status={l.status} inactive={l.inactive} testid="detail-status-badge" />
          </span>
        }
        subtitle={`${l.customerName}${l.customerCode ? ` · HQ ${l.code}` : ""} · Applied ${l.dateApplied}`}
        action={
          <div className="flex flex-wrap gap-2">
            {canEdit && (
              <button data-testid="add-payment-btn" onClick={() => setShowPay((v) => !v)} className="bg-slate-900 text-white hover:bg-slate-800 rounded-md px-4 py-2 text-sm font-medium flex items-center gap-2">
                <PlusCircle className="h-4 w-4" /> {tr("ld.add_payment")}
              </button>
            )}
            <button data-testid="send-reminder-btn" onClick={sendReminder} disabled={sending || !l.phone} title={l.phone ? "Open WhatsApp with a pre-filled reminder" : "Add a phone number first"} className="bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 rounded-md px-4 py-2 text-sm font-medium flex items-center gap-2">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageCircle className="h-4 w-4" />} WhatsApp Reminder
            </button>
            <button data-testid="print-statement-btn" onClick={() => printStatement(l, data.payments)} className="bg-white border border-slate-300 hover:bg-slate-50 rounded-md px-4 py-2 text-sm font-medium flex items-center gap-2">
              <Printer className="h-4 w-4" /> Print Statement
            </button>
            {!canEdit ? null : l.status === "Bad Debt" ? (
              isAdmin ? (
                <button data-testid="reactivate-btn" onClick={reactivate} className="bg-white border border-slate-300 hover:bg-slate-50 rounded-md px-4 py-2 text-sm font-medium">Re-activate</button>
              ) : null
            ) : l.badDebtPending ? (
              <button data-testid="bad-debt-pending-btn" disabled title="Awaiting director approval" className="bg-amber-50 border border-amber-300 text-amber-700 rounded-md px-4 py-2 text-sm font-medium flex items-center gap-2 cursor-default">
                <Loader2 className="h-4 w-4" /> Bad Debt Pending
              </button>
            ) : (
              <button data-testid="mark-bad-debt-btn" onClick={() => setShowBadDebt(true)} className="bg-white border border-red-300 text-red-600 hover:bg-red-50 rounded-md px-4 py-2 text-sm font-medium flex items-center gap-2">
                <Ban className="h-4 w-4" /> Mark Bad Debt
              </button>
            )}
          </div>
        }
      />

      <div className={`grid grid-cols-2 md:grid-cols-5 gap-4 mb-6 ${flash ? "flash-update" : ""}`}>
        <Stat label="Loan Apply" value={fmtRM(l.loanApply)} />
        <Stat label="In Hand" value={fmtRM(l.inHand)} />
        <Stat label="Outstanding" value={fmtRM(l.outstanding)} accent="text-amber-600" />
        <Stat label="Pending Interest" value={fmtRM(l.pendingInterest)} accent={l.pendingInterest > 0 ? "text-red-600" : "text-slate-900"} />
        <Stat label="Actual P/L" value={fmtRM(l.actualProfitLoss)} accent={l.actualProfitLoss >= 0 ? "text-green-600" : "text-red-600"} />
      </div>

      {l.directorSharePct > 0 && (
        <div data-testid="director-share-banner" className="mb-6 bg-indigo-50 border border-indigo-200 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center rounded-full bg-indigo-600 text-white text-xs font-semibold px-3 py-1">
              Shared {l.directorSharePct}% with Director {l.directorName || "—"}
            </span>
            <span className="text-sm text-slate-600">Figures shown are this branch's {100 - l.directorSharePct}% share of the loan.</span>
          </div>
          <div className="flex gap-6">
            {l.fullLoanApply ? (
              <div className="text-right">
                <div className="text-xs text-slate-500">Full Customer Loan</div>
                <div data-testid="full-loan-amount" className="text-base font-bold text-slate-700">{fmtRM(l.fullLoanApply)}</div>
              </div>
            ) : null}
            <div className="text-right">
              <div className="text-xs text-slate-500">Branch Share ({100 - l.directorSharePct}%)</div>
              <div data-testid="branch-share-amount" className="text-base font-bold text-indigo-700">{fmtRM(l.loanApply)}</div>
            </div>
          </div>
        </div>
      )}

      {l.pendingInterest > 0 && (
        <div data-testid="pending-interest-remark" className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <div className="h-8 w-8 rounded-md bg-red-100 flex items-center justify-center shrink-0">
            <span className="text-red-600 font-bold">!</span>
          </div>
          <div className="text-sm">
            <div className="font-semibold text-red-700">Customer has pending interest of {fmtRM(l.pendingInterest)}</div>
            <div className="text-slate-600 mt-0.5 font-mono-fig text-xs">
              {l.monthsAccrued} months accrued × {fmtRM(l.monthlyInterest)} = {fmtRM(l.expectedInterest)} expected − {fmtRM(l.paidInterest)} paid
            </div>
          </div>
        </div>
      )}

      {l.status === "Bad Debt" && (
        <div data-testid="bad-debt-banner" className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <div className="h-8 w-8 rounded-md bg-red-100 flex items-center justify-center shrink-0">
            <Ban className="text-red-600 h-4 w-4" />
          </div>
          <div className="text-sm">
            <div className="font-semibold text-red-700">Bad Debt — {l.badDebtReason || "no reason recorded"}</div>
            {l.badDebtNote && <div className="text-slate-600 mt-0.5">{l.badDebtNote}</div>}
            <div className="text-slate-500 mt-0.5 text-xs">
              {l.badDebtRequestedBy ? `Requested by ${l.badDebtRequestedBy} · ` : ""}Approved by {l.badDebtBy || "—"}
              {l.badDebtDate ? ` on ${new Date(l.badDebtDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}` : ""}
            </div>
          </div>
        </div>
      )}

      {showPay && canEdit && (
        <form onSubmit={addPayment} data-testid="payment-form" className="bg-slate-50 border border-slate-200 rounded-lg p-5 mb-6 grid grid-cols-1 sm:grid-cols-4 gap-4 items-end fade-in">
          <div>
            <label className="text-xs font-semibold uppercase text-slate-500">Date</label>
            <input type="date" data-testid="payment-date-input" className={`${inputCls} mt-1.5`} value={pay.date} onChange={(e) => setPay({ ...pay, date: e.target.value })} />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase text-slate-500">Income (Interest)</label>
            <input type="number" step="0.01" data-testid="payment-income-input" className={`${inputCls} mt-1.5`} value={pay.incomeAmount} onChange={(e) => setPay({ ...pay, incomeAmount: e.target.value })} placeholder="0.00" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase text-slate-500">Principal</label>
            <input type="number" step="0.01" data-testid="payment-principal-input" className={`${inputCls} mt-1.5`} value={pay.principalAmount} onChange={(e) => setPay({ ...pay, principalAmount: e.target.value })} placeholder="0.00" />
          </div>
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <div className="text-xs uppercase text-slate-500 font-semibold">Total</div>
              <div data-testid="payment-total-preview" className="font-mono-fig font-bold text-slate-900 text-lg">{fmtRM(total)}</div>
            </div>
            <button data-testid="payment-save-btn" type="submit" disabled={saving} className="bg-slate-900 text-white rounded-md px-4 py-2 text-sm font-medium flex items-center gap-2 disabled:opacity-60">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} {tr("ld.save")}
            </button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Payment history */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg shadow-sm">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="font-heading font-semibold text-slate-900">{tr("ld.payment_history")}</h3>
            <span className="text-xs text-slate-400">{data.payments.length} entries · Paid {fmtRM(data.payments.reduce((s, p) => s + (Number(p.totalPayment) || 0), 0))}</span>
          </div>
          <table className="w-full text-sm" data-testid="payment-history-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                <th className="px-5 py-2.5 font-medium">Date</th>
                <th className="px-3 py-2.5 font-medium text-right">Income</th>
                <th className="px-3 py-2.5 font-medium text-right">Principal</th>
                <th className="px-3 py-2.5 font-medium text-right">Total</th>
                <th className="px-5 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {data.payments.map((p) => (
                <tr key={p.paymentId} className="border-b border-slate-50 hover:bg-slate-50">
                  <td className="px-5 py-3 text-slate-600">{p.date}</td>
                  <td className="px-3 py-3 text-right font-mono-fig text-green-600">{fmtRM(p.incomeAmount)}</td>
                  <td className="px-3 py-3 text-right font-mono-fig text-slate-800">{fmtRM(p.principalAmount)}</td>
                  <td className="px-3 py-3 text-right font-mono-fig font-semibold text-slate-900">{fmtRM(p.totalPayment)}</td>
                  <td className="px-5 py-3 text-right">
                    <button onClick={() => printReceipt(l, p)} title="Print receipt" className="text-slate-300 hover:text-slate-900 mr-2"><Receipt className="h-3.5 w-3.5 inline" /></button>
                    {canEdit && <button onClick={() => removePayment(p.paymentId)} className="text-slate-300 hover:text-red-600"><Trash2 className="h-3.5 w-3.5 inline" /></button>}
                  </td>
                </tr>
              ))}
              {data.payments.length === 0 && (
                <tr><td colSpan={5} className="px-5 py-10 text-center text-slate-400">No payments recorded yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Loan details */}
        <div className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-lg p-5 text-sm space-y-2.5" data-testid="customer-details-card">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-heading font-semibold text-slate-900">Customer Details</h3>
              {canEdit && !editInfo && <button data-testid="edit-info-btn" onClick={startEditInfo} className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1"><Pencil className="h-3 w-3" /> Edit</button>}
            </div>
            {editInfo ? (
              <div className="space-y-2">
                <input data-testid="edit-phone-input" className={inputCls} placeholder="Phone (WhatsApp) e.g. 012-3456789" value={info.phone} onChange={(e) => setInfo({ ...info, phone: e.target.value })} />
                <input data-testid="edit-ic-input" className={inputCls} placeholder="IC / Passport No." value={info.icPassport} onChange={(e) => setInfo({ ...info, icPassport: e.target.value })} />
                <input data-testid="edit-address-input" className={inputCls} placeholder="Address" value={info.address} onChange={(e) => setInfo({ ...info, address: e.target.value })} />
                <div className="flex gap-2 pt-1">
                  <button data-testid="save-info-btn" onClick={saveInfo} className="bg-slate-900 text-white rounded-md px-3 py-1.5 text-xs font-medium">Save</button>
                  <button onClick={() => setEditInfo(false)} className="border border-slate-300 rounded-md px-3 py-1.5 text-xs">Cancel</button>
                </div>
              </div>
            ) : (
              <>
                <Row k="Phone" v={l.phone || "—"} />
                <Row k="IC / Passport" v={l.icPassport || "—"} />
                <Row k="Address" v={l.address || "—"} />
                {l.agent ? <Row k="Agent" v={l.agent} /> : null}
              </>
            )}
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-5 text-sm space-y-2.5" data-testid="payment-schedule-card">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-heading font-semibold text-slate-900">Payment Schedule</h3>
              {canEdit && !editType && <button data-testid="edit-schedule-btn" onClick={startEditType} className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1"><Pencil className="h-3 w-3" /> Edit</button>}
            </div>
            {editType ? (
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-semibold uppercase text-slate-500">Loan Type</label>
                  <div className="flex gap-2 mt-1.5">
                    <button type="button" data-testid="type-personal-btn" onClick={() => setTypeForm({ ...typeForm, loanType: "personal" })} className={`flex-1 rounded-md px-3 py-1.5 text-xs font-medium border ${typeForm.loanType === "personal" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300"}`}>Personal</button>
                    <button type="button" data-testid="type-business-btn" onClick={() => setTypeForm({ ...typeForm, loanType: "business" })} className={`flex-1 rounded-md px-3 py-1.5 text-xs font-medium border ${typeForm.loanType === "business" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300"}`}>Business</button>
                  </div>
                </div>
                {typeForm.loanType === "personal" ? (
                  <div>
                    <label className="text-xs font-semibold uppercase text-slate-500">Salary Day (1–31)</label>
                    <input type="number" min="1" max="31" data-testid="edit-due-day-input" className={`${inputCls} mt-1.5`} placeholder="e.g. 28" value={typeForm.dueDay} onChange={(e) => setTypeForm({ ...typeForm, dueDay: e.target.value })} />
                    <p className="text-xs text-slate-400 mt-1">Payment due on this day each month.</p>
                  </div>
                ) : (
                  <div>
                    <label className="text-xs font-semibold uppercase text-slate-500">Cycle (Days)</label>
                    <input type="number" min="1" data-testid="edit-cycle-days-input" className={`${inputCls} mt-1.5`} placeholder="e.g. 30" value={typeForm.cycleDays} onChange={(e) => setTypeForm({ ...typeForm, cycleDays: e.target.value })} />
                    <p className="text-xs text-slate-400 mt-1">Due every N days, rolling from the last payment.</p>
                  </div>
                )}
                <div>
                  <label className="text-xs font-semibold uppercase text-slate-500">Next Due Date (override)</label>
                  <input type="date" data-testid="edit-next-due-input" className={`${inputCls} mt-1.5`} value={typeForm.nextDue} onChange={(e) => setTypeForm({ ...typeForm, nextDue: e.target.value })} />
                  <p className="text-xs text-slate-400 mt-1">
                    Auto schedule: <span className="font-mono-fig text-slate-600">{autoNextDueISO({ loanType: typeForm.loanType, dueDay: typeForm.dueDay, cycleDays: typeForm.cycleDays, dateApplied: l.dateApplied }, data.payments) ? fmtNice(autoNextDueISO({ loanType: typeForm.loanType, dueDay: typeForm.dueDay, cycleDays: typeForm.cycleDays, dateApplied: l.dateApplied }, data.payments)) : "—"}</span>. Leave blank to follow it; set a date to override the first payment (applies until the first payment is recorded).
                  </p>
                </div>
                <div className="flex gap-2 pt-1">
                  <button data-testid="save-schedule-btn" onClick={saveType} className="bg-slate-900 text-white rounded-md px-3 py-1.5 text-xs font-medium">Save</button>
                  <button onClick={() => setEditType(false)} className="border border-slate-300 rounded-md px-3 py-1.5 text-xs">Cancel</button>
                </div>
              </div>
            ) : (
              <>
                <Row k="Loan Type" v={(l.loanType || "personal") === "business" ? "Business" : "Personal"} />
                {(l.loanType || "personal") === "business"
                  ? <Row k="Cycle" v={l.cycleDays ? `${l.cycleDays} days` : "—"} />
                  : <Row k="Salary Day" v={l.dueDay ? `Day ${l.dueDay}` : "—"} />}
                <Row k="Next Due Date" v={nextDue || "—"} />
              </>
            )}
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-5 text-sm space-y-2.5" data-testid="loan-details-card">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-heading font-semibold text-slate-900">Loan Details</h3>
              {canEdit && !editDetails && <button data-testid="edit-details-btn" onClick={startEditDetails} className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1"><Pencil className="h-3 w-3" /> Edit</button>}
            </div>
            {editDetails ? (
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-semibold uppercase text-slate-500">Stamping (RM)</label>
                  <input type="number" min="0" step="0.01" data-testid="edit-stamping-input" className={`${inputCls} mt-1.5`} value={detailsForm.stamping} onChange={(e) => setDetailsForm({ ...detailsForm, stamping: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase text-slate-500">Commission (RM)</label>
                  <input type="number" min="0" step="0.01" data-testid="edit-commission-input" className={`${inputCls} mt-1.5`} value={detailsForm.commission} onChange={(e) => setDetailsForm({ ...detailsForm, commission: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase text-slate-500">Risk Tag</label>
                  <select data-testid="edit-risktag-select" className={`${inputCls} mt-1.5`} value={detailsForm.riskTag} onChange={(e) => setDetailsForm({ ...detailsForm, riskTag: e.target.value })}>
                    {RISK_TAGS.map((r) => <option key={r} value={r}>{tv(r)}</option>)}
                  </select>
                </div>
                <p className="text-xs text-slate-400">
                  Money Used recalculates automatically: In Hand = Loan − Stamping, Money Used = In Hand + Commission.
                </p>
                <div className="flex gap-2 pt-1">
                  <button data-testid="save-details-btn" onClick={saveDetails} className="bg-slate-900 text-white rounded-md px-3 py-1.5 text-xs font-medium">Save</button>
                  <button data-testid="cancel-details-btn" onClick={() => setEditDetails(false)} className="border border-slate-300 rounded-md px-3 py-1.5 text-xs">Cancel</button>
                </div>
              </div>
            ) : (
              <>
                <Row k="Stamping" v={fmtRM(l.stamping)} />
                <Row k="Commission" v={fmtRM(l.commission)} />
                <Row k="Money Used" v={fmtRM(l.moneyUsed)} />
                <Row k="Risk Tag" v={<RiskBadge riskTag={l.riskTag} />} />
              </>
            )}
            <div className="pt-2 border-t border-slate-100">
              <div className="text-xs uppercase text-slate-500 font-semibold mb-1">Repayment Formula</div>
              <div className="font-mono-fig text-slate-900">{l.repaymentFormula || "—"}</div>
              {parsed.valid && <div className="text-xs text-slate-500 mt-1">{formulaLabel(parsed.type)}{parsed.monthlyInterest ? ` · Interest ${fmtRM(parsed.monthlyInterest)}/mo` : ""}</div>}
            </div>
            {l.notes && (
              <div className="pt-2 border-t border-slate-100">
                <div className="text-xs uppercase text-slate-500 font-semibold mb-1">Notes</div>
                <div className="text-slate-700">{l.notes}</div>
              </div>
            )}
          </div>
          {canEdit && (
            <button data-testid="delete-loan-btn" onClick={deleteLoan} className="w-full text-sm text-red-600 border border-red-200 hover:bg-red-50 rounded-md px-4 py-2 flex items-center justify-center gap-2">
              <Trash2 className="h-4 w-4" /> Delete Loan
            </button>
          )}
        </div>
      </div>

      {showBadDebt && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowBadDebt(false)}>
          <div data-testid="bad-debt-modal" className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-1">
              <Ban className="h-5 w-5 text-red-600" />
              <h3 className="font-heading font-semibold text-slate-900">{tr("ld.mark_bad_debt")}</h3>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              {isAdmin
                ? "This will write off the loan and remove it from the collection workflow."
                : "This sends a request to the director. The loan is written off only after they approve it."}
            </p>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase text-slate-500">Reason</label>
                <select data-testid="bad-debt-reason-select" className={`${inputCls} mt-1.5`}
                  value={bdForm.reason} onChange={(e) => setBdForm({ ...bdForm, reason: e.target.value })}>
                  {BAD_DEBT_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase text-slate-500">Note (optional)</label>
                <textarea data-testid="bad-debt-note-input" rows={3} className={`${inputCls} mt-1.5`}
                  placeholder="Any extra detail…" value={bdForm.note} onChange={(e) => setBdForm({ ...bdForm, note: e.target.value })} />
              </div>
              <div className="flex gap-2 justify-end pt-1">
                <button onClick={() => setShowBadDebt(false)} className="border border-slate-300 rounded-md px-4 py-2 text-sm">Cancel</button>
                <button data-testid="bad-debt-submit" onClick={submitBadDebt} className="bg-red-600 text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-red-700">
                  {isAdmin ? "Mark Bad Debt" : "Send Request"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-slate-500">{k}</span>
      <span className="font-mono-fig text-slate-900">{v}</span>
    </div>
  );
}

function computeNextDue(loan, payments) {
  if (!loan || (loan.outstanding != null && loan.outstanding <= 0)) return null;
  const hasPay = (payments || []).some((p) => p.date);
  if (loan.nextDueOverride && !hasPay) return `${fmtNice(loan.nextDueOverride)} · manual`;
  const iso = autoNextDueISO(loan, payments);
  return iso ? fmtNice(iso) : null;
}

function fmtNice(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function autoNextDueISO(loan, payments) {
  const type = loan.loanType || "personal";
  const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  if (type === "business") {
    const cycle = parseInt(loan.cycleDays, 10);
    if (!cycle) return "";
    const dates = (payments || []).map((p) => p.date).filter(Boolean).sort();
    const baseStr = dates.length ? dates[dates.length - 1] : loan.dateApplied;
    if (!baseStr) return "";
    const base = new Date(baseStr + "T00:00:00");
    base.setDate(base.getDate() + cycle);
    return toISO(base);
  }
  const day = parseInt(loan.dueDay, 10);
  if (!day) return "";
  const now = new Date();
  const clamp = (yy, mm) => Math.min(day, new Date(yy, mm + 1, 0).getDate());
  let due = new Date(now.getFullYear(), now.getMonth(), clamp(now.getFullYear(), now.getMonth()));
  if (due < new Date(now.getFullYear(), now.getMonth(), now.getDate())) {
    due = new Date(now.getFullYear(), now.getMonth() + 1, clamp(now.getFullYear(), now.getMonth() + 1));
  }
  return toISO(due);
}
